import { fetchOperationalPlanById } from './operationalPlansApi';
import logoUrl from './logo.svg';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function esc(v) {
  if (v == null) return '';
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function dash(v) {
  if (v === null || v === undefined) return '—';
  const s = String(v).trim();
  return s ? esc(s) : '—';
}

function fmtDate(v) {
  if (!v) return '—';
  return esc(String(v).slice(0, 10));
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === '') return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return dash(n);
  return esc(
    x.toLocaleString('ar-SA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  );
}

function planStatusAr(status) {
  const s = (status || '').toLowerCase().trim();
  const map = {
    draft: 'مسودة',
    under_review: 'قيد المراجعة',
    approved: 'معتمدة',
    in_progress: 'جاري التنفيذ',
    completed: 'مكتملة',
  };
  return map[s] || dash(status);
}

function activityStatusAr(s) {
  const x = (s || '').toLowerCase().trim();
  const map = {
    planned: 'مخطط',
    in_progress: 'جاري التنفيذ',
    done: 'منجز',
    paused: 'موقوف',
    cancelled: 'ملغى',
  };
  return map[x] || dash(s);
}

function sortBySortOrder(arr) {
  return [...(arr || [])].sort(
    (a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0)
  );
}

function arabicOrdinal(n) {
  const words = [
    'الأول', 'الثاني', 'الثالث', 'الرابع', 'الخامس',
    'السادس', 'السابع', 'الثامن', 'التاسع', 'العاشر',
  ];
  return words[n - 1] || String(n);
}

/** Aligns with src/index.css — primary / secondary on printed output */
const PRINT_THEME = {
  primary: '#02c3a6',
  primaryDark: '#029e88',
  secondary: '#00334a',
  secondaryLight: '#004d6e',
  border: '#cef0eb',
  borderStrong: '#9dd9ce',
  surfaceSoft: '#e6faf7',
  surfacePage: '#f0f8f7',
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

function buildStyles() {
  const t = PRINT_THEME;
  return `
    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&family=Tajawal:wght@400;700&display=swap');

    :root {
      --pdf-primary: ${t.primary};
      --pdf-primary-dark: ${t.primaryDark};
      --pdf-secondary: ${t.secondary};
      --pdf-secondary-light: ${t.secondaryLight};
      --pdf-border: ${t.border};
      --pdf-border-strong: ${t.borderStrong};
      --pdf-surface-soft: ${t.surfaceSoft};
      --pdf-surface-page: ${t.surfacePage};
    }

    @page {
      size: A4 portrait;
      margin: 0;
    }

    *, *::before, *::after {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html, body {
      background: white;
      font-family: 'Cairo', 'Tajawal', 'Segoe UI', 'Arial', 'Tahoma', sans-serif;
      direction: rtl;
    }

    @media print {
      html, body {
        width: 210mm;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      .page {
        page-break-after: always;
        page-break-inside: avoid;
        break-after: page;
        break-inside: avoid;
      }
      .page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
    }

    .print-container {
      background: white;
    }

    /* ── Page shell ── */
    .page {
      width: 210mm;
      min-height: 297mm;
      padding: 8mm;
      background: white;
      display: flex;
      flex-direction: column;
    }

    .page-border {
      border: 2px solid var(--pdf-secondary);
      min-height: calc(297mm - 16mm);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: #fff;
    }

    /* ── Header ── */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 20px;
      border-bottom: 3px solid var(--pdf-primary);
      background: linear-gradient(to bottom, var(--pdf-surface-soft), #ffffff);
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .logo {
      height: 55px;
      width: auto;
      max-width: 120px;
      object-fit: contain;
    }

    .org-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .org-name-en {
      font-size: 14px;
      font-weight: 700;
      color: var(--pdf-secondary);
      letter-spacing: 0.5px;
    }

    .org-name-ar {
      font-size: 12px;
      font-weight: 700;
      color: var(--pdf-secondary);
    }

    .header-center {
      text-align: center;
    }

    .doc-title {
      font-size: 20px;
      font-weight: 800;
      color: var(--pdf-secondary);
    }

    .doc-subtitle {
      font-size: 10px;
      color: var(--pdf-primary-dark);
      font-weight: 600;
      margin-top: 2px;
    }

    .header-right {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: 9px;
      text-align: right;
    }

    .meta-row {
      display: flex;
      gap: 5px;
      align-items: center;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .meta-label { color: var(--pdf-secondary-light); }

    .meta-val {
      font-weight: 700;
      color: var(--pdf-secondary);
    }

    /* ── Content / Footer ── */
    .content {
      flex: 1;
      padding: 14px 20px;
    }

    .footer {
      padding: 8px 20px 12px;
    }

    .footer-line {
      height: 1px;
      background: linear-gradient(to left, transparent, var(--pdf-border-strong), transparent);
      margin-bottom: 6px;
    }

    .footer-text {
      text-align: center;
      font-size: 9px;
      color: var(--pdf-secondary-light);
    }

    /* ── Section titles ── */
    .section {
      margin-bottom: 16px;
    }

    .section-title {
      font-size: 12px;
      font-weight: 700;
      color: #fff;
      padding: 8px 15px;
      background: var(--pdf-primary);
      border-inline-start: 4px solid var(--pdf-secondary);
      margin-bottom: 10px;
    }

    .section-title-alt {
      margin-top: 10px;
    }

    /* ── Meta table (plan info) ── */
    .meta-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 10px;
      margin-bottom: 14px;
    }

    .meta-table th,
    .meta-table td {
      border: 1px solid var(--pdf-border);
      padding: 8px 10px;
      vertical-align: middle;
    }

    .meta-table th {
      background: var(--pdf-surface-soft);
      color: var(--pdf-secondary);
      font-weight: 700;
      width: 18%;
      white-space: nowrap;
    }

    .meta-table td {
      background: #fff;
      color: var(--pdf-secondary);
      font-weight: 600;
    }

    /* ── Sub-headings inside sections ── */
    .sub-head {
      background: var(--pdf-surface-soft);
      color: var(--pdf-secondary);
      font-size: 10px;
      font-weight: 700;
      padding: 6px 12px;
      margin: 12px 0 0;
      border: 1px solid var(--pdf-border);
      border-inline-start: 3px solid var(--pdf-primary);
      border-bottom: none;
    }

    /* ── Inner key-value table ── */
    .inner-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 10px;
      margin-bottom: 8px;
    }

    .inner-table th {
      background: var(--pdf-primary);
      color: #fff;
      font-weight: 700;
      padding: 8px 10px;
      width: 22%;
      border: 1px solid var(--pdf-border-strong);
    }

    .inner-table td {
      padding: 8px 12px;
      border: 1px solid var(--pdf-border-strong);
      background: var(--pdf-surface-soft);
      color: var(--pdf-secondary);
      line-height: 1.55;
    }

    /* ── Data tables ── */
    .data-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9px;
    }

    .data-table th {
      background: var(--pdf-primary);
      color: #fff;
      padding: 8px 7px;
      font-weight: 700;
      text-align: center;
      border: 1px solid var(--pdf-border-strong);
    }

    .data-table td {
      padding: 7px 8px;
      border: 1px solid var(--pdf-border-strong);
      vertical-align: top;
      background: #fff;
      color: var(--pdf-secondary);
    }

    .data-table tr:nth-child(even) td {
      background: var(--pdf-surface-soft);
    }

    .data-table td.muted {
      color: #999;
      font-style: italic;
      text-align: center;
    }

    /* ── Axis blocks ── */
    .axis-block {
      margin-bottom: 14px;
    }

    .axis-head {
      background: var(--pdf-secondary);
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      padding: 8px 12px;
      border: 1px solid var(--pdf-secondary);
      border-inline-start: 4px solid var(--pdf-primary);
    }

    /* ── Misc ── */
    .bullet-list {
      padding-right: 18px;
      font-size: 10px;
      line-height: 1.75;
      color: var(--pdf-secondary);
    }

    .bullet-list li {
      margin-bottom: 5px;
    }

    .intro-text {
      font-size: 10px;
      color: #555;
      padding: 4px 2px 8px;
      margin-bottom: 4px;
      line-height: 1.6;
    }

    .intro-status {
      margin-top: 8px;
      font-size: 9px;
      color: #666;
    }

    /* ── Signatures ── */
    .signatures {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-top: 12px;
    }

    .sig-box {
      text-align: center;
      padding: 12px;
      border: 1px dashed var(--pdf-primary);
      border-radius: 4px;
      background: var(--pdf-surface-soft);
      font-size: 9px;
    }

    .sig-title {
      font-weight: 700;
      color: var(--pdf-secondary);
      font-size: 11px;
      margin-bottom: 28px;
    }

    .sig-line {
      border-top: 1px solid var(--pdf-primary-dark);
      margin: 0 12px;
    }

    .sig-hint {
      font-size: 8px;
      color: #888;
      margin-top: 6px;
    }

    .sig-lbl {
      color: #666;
      margin-top: 8px;
      font-size: 8px;
      text-align: right;
    }

    .sig-val {
      min-height: 18px;
      border-bottom: 1px dotted var(--pdf-primary);
      margin-top: 2px;
      text-align: center;
      padding: 2px 4px;
      font-weight: 600;
      color: var(--pdf-secondary);
    }
  `;
}

// ---------------------------------------------------------------------------
// Page shell builder
// ---------------------------------------------------------------------------

function createPage(bodyHtml, pageNum, totalPages, { planId, orgNameAr, orgNameEng, printDate }) {
  return `
    <div class="page">
      <div class="page-border">

        <div class="header">
          <div class="header-left">
            <img src="${logoUrl}" alt="logo" class="logo">
            <div class="org-info">
              <div class="org-name-en">${orgNameEng}</div>
              <div class="org-name-ar">${orgNameAr}</div>
            </div>
          </div>

          <div class="header-center">
            <div class="doc-title">الخطة التشغيلية</div>
            <div class="doc-subtitle">Operational Plan</div>
          </div>

          <div class="header-right">
            <div class="meta-row"><span class="meta-label">الخطة التشغيلية — إصدار رسمي</span></div>
            <div class="meta-row">
              <span class="meta-label">معرّف الخطة:</span>
              <span class="meta-val" dir="ltr">${esc(planId)}</span>
            </div>
            <div class="meta-row">
              <span class="meta-label">صفحة:</span>
              <span class="meta-val">${pageNum} / ${totalPages}</span>
            </div>
          </div>
        </div>

        <div class="content">
          ${bodyHtml}
        </div>

        <div class="footer">
          <div class="footer-line"></div>
          <div class="footer-text">
            تاريخ الطباعة: ${printDate} | ${orgNameAr} — نظام كورب باس
          </div>
        </div>

      </div>
    </div>`;
}

// ---------------------------------------------------------------------------
// HTML builder
// ---------------------------------------------------------------------------

function buildDocumentHTML(plan, periodSafeHtml, oversightDeptPlain, overviewBullets) {
  const objectives  = sortBySortOrder(plan.objectives);
  const axes        = sortBySortOrder(plan.axes);
  const hr          = sortBySortOrder(plan.hrResources);
  const financial   = sortBySortOrder(plan.financialResources);
  const kpis        = sortBySortOrder(plan.kpis);
  const risks       = sortBySortOrder(plan.risks);

  const printDate = new Date().toLocaleDateString('ar-SA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  });

  const meta = {
    planId:     plan.id ?? plan.Id ?? '—',
    orgNameAr:  esc(plan.companyName || '—'),
    orgNameEng: 'Corp Pass',
    printDate,
  };

  const TOTAL_PAGES = 3;

  // ── Overview bullets ──────────────────────────────────────────────────────
  const bulletsHtml = overviewBullets.length === 0
    ? '<p style="color:#999;font-style:italic;font-size:10px">—</p>'
    : `<ul class="bullet-list">${overviewBullets
        .map((t) => `<li>${esc(String(t))}</li>`)
        .join('')}</ul>`;

  // ── Objectives table ──────────────────────────────────────────────────────
  const objectiveRows = objectives.length === 0
    ? '<tr><td colspan="4" class="muted">— لا توجد أهداف —</td></tr>'
    : objectives.map((o, i) => `
        <tr>
          <td style="text-align:center;width:36px">${i + 1}</td>
          <td>${dash(o.objectiveText)}</td>
          <td>${dash(o.successIndicator)}</td>
          <td>${dash(o.responsibleDepartmentName)}</td>
        </tr>`).join('');

  // ── Axes + activities ─────────────────────────────────────────────────────
  const axesHTML = axes.length === 0
    ? '<div class="muted" style="text-align:center;padding:12px">— لا توجد محاور أو أنشطة —</div>'
    : axes.map((ax, axIdx) => {
        const acts = sortBySortOrder(ax.activities);
        const actRows = acts.length === 0
          ? '<tr><td colspan="5" class="muted">— لا أنشطة —</td></tr>'
          : acts.map((a) => `
              <tr>
                <td>
                  <strong>${dash(a.name)}</strong>
                  ${a.description
                    ? `<br><span style="color:var(--pdf-secondary-light);font-size:8pt">${esc(String(a.description))}</span>`
                    : ''}
                </td>
                <td style="white-space:nowrap;text-align:center" dir="ltr">${fmtDate(a.startDateUtc)}</td>
                <td style="white-space:nowrap;text-align:center" dir="ltr">${fmtDate(a.endDateUtc)}</td>
                <td style="text-align:center">—</td>
                <td style="text-align:center">${activityStatusAr(a.status)}</td>
              </tr>`).join('');

        const dept = ax.departmentName ? ` (${esc(ax.departmentName)})` : '';
        return `
          <div class="axis-block">
            <div class="axis-head">المحور ${arabicOrdinal(axIdx + 1)}: ${esc(ax.name || '')}${dept}</div>
            <table class="data-table">
              <thead>
                <tr>
                  <th>النشاط</th>
                  <th style="width:80px">تاريخ البدء</th>
                  <th style="width:80px">تاريخ الانتهاء</th>
                  <th style="width:90px">الميزانية التقديرية</th>
                  <th style="width:80px">الحالة</th>
                </tr>
              </thead>
              <tbody>${actRows}</tbody>
            </table>
          </div>`;
      }).join('');

  // ── HR rows ───────────────────────────────────────────────────────────────
  const hrRows = hr.length === 0
    ? '<tr><td colspan="4" class="muted">—</td></tr>'
    : hr.map((h) => `
        <tr>
          <td>${dash(h.role)}</td>
          <td style="text-align:center">${h.numberRequired != null ? esc(String(h.numberRequired)) : '—'}</td>
          <td>${dash(h.natureOfWork)}</td>
          <td>${dash(h.notes)}</td>
        </tr>`).join('');

  // ── Financial rows ────────────────────────────────────────────────────────
  const financialRows = financial.length === 0
    ? '<tr><td colspan="4" class="muted">—</td></tr>'
    : financial.map((f) => `
        <tr>
          <td>${dash(f.budgetItem)}</td>
          <td dir="ltr" style="text-align:center">${fmtMoney(f.estimatedAmount)}</td>
          <td dir="ltr" style="text-align:center">${fmtMoney(f.actualAmount)}</td>
          <td>${dash(f.notes)}</td>
        </tr>`).join('');

  // ── KPI rows ──────────────────────────────────────────────────────────────
  const kpiRows = kpis.length === 0
    ? '<tr><td colspan="6" class="muted">—</td></tr>'
    : kpis.map((k) => `
        <tr>
          <td>${dash(k.name)}</td>
          <td style="text-align:center">${dash(k.targetValue)}</td>
          <td style="text-align:center">${dash(k.quarter1)}</td>
          <td style="text-align:center">${dash(k.quarter2)}</td>
          <td style="text-align:center">${dash(k.quarter3)}</td>
          <td>${dash(k.measurementMethod)}</td>
        </tr>`).join('');

  // ── Risk rows ─────────────────────────────────────────────────────────────
  const riskRows = risks.length === 0
    ? '<tr><td colspan="4" class="muted">— لا مخاطر مسجلة —</td></tr>'
    : risks.map((r) => `
        <tr>
          <td>${dash(r.description)}</td>
          <td style="text-align:center">${dash(r.probability)}</td>
          <td style="text-align:center">${dash(r.impact)}</td>
          <td>${dash(r.mitigationPlan)}</td>
        </tr>`).join('');

  // ── Page 1 ────────────────────────────────────────────────────────────────
  const page1Body = `
    <table class="meta-table">
      <tbody>
        <tr>
          <th>اسم المؤسسة / الجهة</th>
          <td>${dash(plan.companyName)}</td>
          <th>القطاع / الإدارة</th>
          <td>${dash(plan.departmentName)}</td>
        </tr>
        <tr>
          <th>الفترة الزمنية</th>
          <td dir="ltr" style="text-align:right">${periodSafeHtml}</td>
          <th>تاريخ الاعتماد</th>
          <td>${plan.approvalConfirmed ? fmtDate(plan.updatedAtUtc) : '— بانتظار الاعتماد —'}</td>
        </tr>
        <tr>
          <th>معد الخطة</th>
          <td>${dash(plan.preparedBy)}</td>
          <th>المعتمد من</th>
          <td>${dash(plan.approvedBy)}</td>
        </tr>
      </tbody>
    </table>

    <div class="section">
      <div class="section-title">أولاً: نظرة عامة على الخطة</div>
      <div style="padding:0 4px 8px">
        <div style="font-weight:800;font-size:10px;margin-bottom:6px;color:#333">١.١ مقدمة</div>
        ${bulletsHtml}
        <div class="intro-status">
          حالة الخطة: <strong>${planStatusAr(plan.status)}</strong>
          — آخر تحديث: <strong dir="ltr">${fmtDate(plan.updatedAtUtc)}</strong>
        </div>
      </div>

      <div class="section-title section-title-alt">١.٢ الرؤية والرسالة والقيم</div>
      <table class="inner-table">
        <tbody>
          <tr><th>الرؤية</th><td>${dash(plan.vision)}</td></tr>
          <tr><th>الرسالة</th><td>${dash(plan.mission)}</td></tr>
          <tr><th>القيم</th><td>${dash(plan.valuesText)}</td></tr>
        </tbody>
      </table>
    </div>

    <div class="section">
      <div class="section-title">ثانياً: الأهداف التشغيلية</div>
      <p class="intro-text">تستهدف الخطة التشغيلية تحقيق الأهداف التالية خلال الفترة الزمنية المحددة:</p>
      <table class="data-table">
        <thead>
          <tr>
            <th style="width:36px">#</th>
            <th>الهدف التشغيلي</th>
            <th>مؤشر النجاح</th>
            <th>الجهة المسؤولة</th>
          </tr>
        </thead>
        <tbody>${objectiveRows}</tbody>
      </table>
    </div>`;

  // ── Page 2 ────────────────────────────────────────────────────────────────
  const page2Body = `
    <div class="section">
      <div class="section-title">ثالثاً: خطة العمل والأنشطة التفصيلية</div>
      ${axesHTML}
    </div>

    <div class="section">
      <div class="section-title">رابعاً: الموارد المطلوبة</div>

      <div class="sub-head">٤.١ الموارد البشرية</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>الوظيفة / الدور</th>
            <th>العدد المطلوب</th>
            <th>طبيعة العمل</th>
            <th>ملاحظات</th>
          </tr>
        </thead>
        <tbody>${hrRows}</tbody>
      </table>

      <div class="sub-head">٤.٢ الميزانية التقديرية</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>بند الميزانية</th>
            <th>المبلغ المقدر</th>
            <th>المبلغ الفعلي</th>
            <th>ملاحظات</th>
          </tr>
        </thead>
        <tbody>${financialRows}</tbody>
      </table>
    </div>`;

  // ── Page 3 ────────────────────────────────────────────────────────────────
  const page3Body = `
    <div class="section">
      <div class="section-title">خامساً: مؤشرات الأداء الرئيسية (KPIs)</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>مؤشر الأداء</th>
            <th>القيمة المستهدفة</th>
            <th>الربع الأول</th>
            <th>الربع الثاني</th>
            <th>الربع الثالث</th>
            <th>طريقة القياس</th>
          </tr>
        </thead>
        <tbody>${kpiRows}</tbody>
      </table>
    </div>

    <div class="section">
      <div class="section-title">سادساً: آلية المتابعة والتقييم وإدارة المخاطر</div>

      <div class="sub-head" style="margin-top:0">٦.١ متابعة التنفيذ</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>نوع المتابعة</th>
            <th>دورية</th>
            <th>الجهة المسؤولة</th>
            <th>نموذج المتابعة</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>تحقق من تنفيذ الأنشطة ومؤشرات الأداء</td>
            <td style="text-align:center">شهرية</td>
            <td>${dash(oversightDeptPlain)}</td>
            <td>تقارير إنجاز / اجتماع لجنة المتابعة</td>
          </tr>
        </tbody>
      </table>

      <div class="sub-head">٦.٢ إدارة المخاطر</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>المخاطر المحتملة</th>
            <th>درجة الاحتمالية</th>
            <th>درجة الأثر</th>
            <th>خطة التخفيف</th>
          </tr>
        </thead>
        <tbody>${riskRows}</tbody>
      </table>
    </div>

    <div class="section">
      <div class="section-title">سابعاً: الاعتماد والتوقيعات</div>
      <div class="signatures">
        <div class="sig-box">
          <div class="sig-title">معد الخطة</div>
          <div class="sig-lbl">الاسم</div>
          <div class="sig-val">${dash(plan.preparedBy)}</div>
          <div class="sig-line" style="margin-top:14px"></div>
          <div class="sig-hint">التوقيع والتاريخ</div>
        </div>
        <div class="sig-box">
          <div class="sig-title">المراجع</div>
          <div class="sig-lbl">الاسم</div>
          <div class="sig-val">${dash(plan.reviewedBy)}</div>
          <div class="sig-line" style="margin-top:14px"></div>
          <div class="sig-hint">التوقيع والتاريخ</div>
        </div>
        <div class="sig-box">
          <div class="sig-title">المعتمد</div>
          <div class="sig-lbl">الاسم</div>
          <div class="sig-val">${dash(plan.approvedBy)}</div>
          <div class="sig-line" style="margin-top:14px"></div>
          <div class="sig-hint">التوقيع والتاريخ</div>
        </div>
      </div>
    </div>`;

  const pages = [
    createPage(page1Body, 1, TOTAL_PAGES, meta),
    createPage(page2Body, 2, TOTAL_PAGES, meta),
    createPage(page3Body, 3, TOTAL_PAGES, meta),
  ];

  return pages.join('');
}

// ---------------------------------------------------------------------------
// Full HTML document wrapper
// ---------------------------------------------------------------------------

function buildFullHtml(plan, periodSafeHtml, oversightDeptPlain, overviewBullets) {
  const styles = buildStyles();
  const pagesHtml = buildDocumentHTML(plan, periodSafeHtml, oversightDeptPlain, overviewBullets);

  return `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>الخطة التشغيلية — ${esc(plan.companyName || '')}</title>
  <style>${styles}</style>
</head>
<body>
  <div class="print-container">
    ${pagesHtml}
  </div>
  <script>
    // Auto-trigger print after fonts are loaded
    (function () {
      function triggerPrint() {
        setTimeout(function () { window.print(); }, 400);
      }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(triggerPrint);
      } else {
        window.addEventListener('load', triggerPrint);
      }
    })();
  </script>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generates the operational plan as a full HTML document and opens it in a
 * new tab. The browser's native print dialog is triggered automatically so
 * the user can save it as PDF — no html2canvas or jsPDF required.
 *
 * Benefits over the previous html2canvas approach:
 *  - No washed-out / low-opacity rendering
 *  - Arabic fonts render correctly (browser handles them natively)
 *  - Background colors and gradients printed at full fidelity
 *  - No dependency on html2canvas or jsPDF
 */
export const exportOperationalPlanPDF = async (plan, { onStart, onDone, onError } = {}) => {
  const periodSafeHtml = (() => {
    const a = plan.periodStartUtc ? String(plan.periodStartUtc).slice(0, 10) : '';
    const b = plan.periodEndUtc   ? String(plan.periodEndUtc).slice(0, 10)   : '';
    if (!a && !b) return '—';
    if (!a) return esc(b);
    if (!b) return esc(a);
    return `${esc(a)} إلى ${esc(b)}`;
  })();

  const oversightDeptPlain =
    String(plan?.departmentName || plan?.companyName || '—').trim() || '—';

  const overviewBullets = [];
  if (plan.overviewDescription?.trim()) overviewBullets.push(plan.overviewDescription.trim());
  if (plan.overviewPurpose?.trim())     overviewBullets.push(plan.overviewPurpose.trim());

  onStart?.();

  try {
    const fullHtml = buildFullHtml(plan, periodSafeHtml, oversightDeptPlain, overviewBullets);

    const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);

    const newWindow = window.open(url, '_blank');

    if (newWindow) {
      newWindow.focus();
      // Keep the blob URL alive long enough for the print dialog to open,
      // then release the memory.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      // Popup blocked → download the HTML file as fallback.
      // The user can open it in a browser and print from there.
      const pid      = plan.id ?? plan.Id ?? 'plan';
      const filename = `OperationalPlan_${pid}.html`;
      const a        = document.createElement('a');
      a.href         = url;
      a.download     = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }

    onDone?.();
  } catch (error) {
    console.error('[exportOperationalPlanPDF] Error:', error);
    onError?.(error);
    throw error;
  }
};

/**
 * Fetches fresh plan detail by ID and opens the styled PDF-ready page
 * in a new browser tab.
 */
export async function openOperationalPlanPdfInNewTab(planId, callbacks = {}) {
  const raw = await fetchOperationalPlanById(Number(planId));
  return exportOperationalPlanPDF(raw, callbacks);
}